import { Link } from "react-router-dom";
import type { Activity } from "../api/client";
import { DEFAULT_AVATAR } from "../api/client";
import {
  formatApptDate,
  formatDayShort,
  formatMediumDate,
  formatPhone,
  formatTimeParts,
} from "../lib/format";

function isAppointment(item: Activity): boolean {
  return (
    item.activity_type?.startsWith("Appointment") ||
    item.key?.toLowerCase().includes("appointment")
  );
}

function isMessage(item: Activity): boolean {
  return (
    item.activity_type === "Message" ||
    item.key?.toLowerCase().includes("message")
  );
}

function isSurvey(item: Activity): boolean {
  return (
    item.activity_type === "SurveySubmission" ||
    item.key?.toLowerCase().includes("survey")
  );
}

function appointmentVerb(item: Activity): string {
  switch (item.activity_type) {
    case "AppointmentConfirm":
      return "has confirmed an appointment";
    case "AppointmentResched":
      return "has rescheduled an appointment";
    case "AppointmentCancel":
      return "has cancelled an appointment";
    case "AppointmentCheckIn":
      return "just checked in";
    case "AppointmentCreate":
    default:
      if (item.key?.toLowerCase().includes("cancel"))
        return "has cancelled an appointment";
      if (item.key?.toLowerCase().includes("confirm"))
        return "has confirmed an appointment";
      if (item.key?.toLowerCase().includes("resched"))
        return "has rescheduled an appointment";
      if (item.key?.toLowerCase().includes("check")) return "just checked in";
      return "has requested an appointment";
  }
}

function brickIconClass(item: Activity): string {
  if (isMessage(item)) return "ne__brick__icon--appt-message";
  if (isSurvey(item)) return "ne__brick__icon--survey";
  switch (item.activity_type) {
    case "AppointmentConfirm":
      return "ne__brick__icon--appt-confirm";
    case "AppointmentResched":
      return "ne__brick__icon--appt-rescheduled";
    case "AppointmentCancel":
      return "ne__brick__icon--appt-cancelled";
    case "AppointmentCheckIn":
      return "ne__brick__icon--appt-checkin";
    default:
      return "ne__brick__icon--appt-requested";
  }
}

function BrickAside({ item }: { item: Activity }) {
  const { time, period } = formatTimeParts(item.created_at);
  return (
    <aside className="ne__brick__aside">
      <div>
        <p className="ne__brick__time">
          {time}
          <span className="ne__meridian"> {period}</span>
        </p>
        {/* Angular brick day: "MON AUG 3" (no comma; CSS uppercases) */}
        <p className="ne__brick__day">
          {formatDayShort(item.created_at).replace(",", "")}
        </p>
      </div>
      <i className={brickIconClass(item)} />
    </aside>
  );
}

function AvatarFigure({
  src,
  alt,
}: {
  src?: string | null;
  alt?: string;
}) {
  return (
    <div className="ne__avatar__wrapper">
      <figure className="ne__avatar">
        <img src={src || DEFAULT_AVATAR} alt={alt || ""} />
      </figure>
    </div>
  );
}

export function ActivityBrick({ item }: { item: Activity }) {
  const owner = item.owner;
  const ownerId = item.owner_id || owner?.id;
  const messageBody =
    item.parameters?.data ||
    item.parameters?.preview ||
    (typeof item.trackable?.body === "string" ? item.trackable.body : "") ||
    "";

  if (isMessage(item)) {
    return (
      <li className="ne__brick--timeline">
        <div className="ne__brick">
        <BrickAside item={item} />
        <div className="ne__brick__box ne__brick--message">
          <table>
            <thead>
              <tr>
                <td>
                  <AvatarFigure src={owner?.profile_url} alt={owner?.name} />
                </td>
                <th>
                  <h5 className="ne__brick__title">
                    {ownerId ? (
                      <Link className="ne__link" to={`/app/patients/${ownerId}/activity`}>
                        <strong>{owner?.name}</strong>
                      </Link>
                    ) : (
                      <strong>{owner?.name || "Patient"}</strong>
                    )}
                    {owner?.bio?.date_of_birth ? (
                      <span className="ne__date" title={owner.bio.date_of_birth}>
                        {" "}
                        {formatMediumDate(owner.bio.date_of_birth)}
                      </span>
                    ) : null}
                  </h5>
                  {messageBody ? (
                    <blockquote>
                      <p className="ne__brick__message">{messageBody}</p>
                    </blockquote>
                  ) : null}
                </th>
              </tr>
            </thead>
          </table>
          <div className="ne__brick__buttons">
            {ownerId ? (
              <Link
                className="ne__btn ne__btn--secondary ne__btn--md"
                to={`/app/patients/${ownerId}/conversations/archive_conversation_01`}
              >
                Reply
              </Link>
            ) : null}
          </div>
        </div>
        </div>
      </li>
    );
  }

  if (isSurvey(item)) {
    return (
      <li className="ne__brick--timeline">
        <div className="ne__brick">
        <BrickAside item={item} />
        <div className="ne__brick__box ne__brick--survey">
          <table>
            <thead>
              <tr>
                <td>
                  <AvatarFigure src={owner?.profile_url} alt={owner?.name} />
                </td>
                <th>
                  <h5 className="ne__brick__title">
                    {ownerId ? (
                      <Link to={`/app/patients/${ownerId}/activity`}>
                        <strong>{owner?.name}</strong>
                      </Link>
                    ) : (
                      <strong>{owner?.name || "Patient"}</strong>
                    )}{" "}
                    <span>submitted a survey.</span>
                  </h5>
                  {item.parameters?.data ? (
                    <p className="ne__brick__survey-appt-date">
                      Appointment date {formatMediumDate(String(item.parameters.data))}
                    </p>
                  ) : null}
                </th>
              </tr>
            </thead>
          </table>
          <div className="ne__brick__buttons">
            <span className="ne__link ne__link--primary">View survey</span>
          </div>
        </div>
        </div>
      </li>
    );
  }

  // Historical: New Patient when owner has no last_seen for the institution.
  // Synthetic patients with a last_check_in still get the badge on first-request
  // bricks so the timeline matches Angular archive screenshots.
  const isNewPatient =
    item.activity_type === "AppointmentCreate" ||
    item.key?.toLowerCase().includes("request") ||
    (!owner?.last_check_in &&
      !(owner?.last_seen && Object.keys(owner.last_seen).length > 0));
  // Angular: locationhash[trackable.location_id].name → "Cobble Hill"
  const locationName =
    item.parameters?.location_name ||
    (typeof item.trackable?.location_name === "string"
      ? item.trackable.location_name
      : null) ||
    (item.trackable?.location_id ? "Cobble Hill" : null);

  // Angular activitybrick: li > .ne__brick (flex row) > aside + box
  return (
    <li className="ne__brick--timeline">
      <div className="ne__brick">
      <BrickAside item={item} />
      <div
        className={`ne__brick__box ${
          isAppointment(item) ? "ne__brick--appointment" : "ne__brick--message"
        }`}
      >
        <a
          className="ne__btn ne__btn--sm ne__btn--tertiary ne__btn--close ne__btn--icon ne__btn--halo"
          href="#dismiss"
          aria-label="Dismiss"
          onClick={(e) => e.preventDefault()}
        >
          <i className="ne__icon__x--thick" />
        </a>
        <table>
          <thead>
            <tr>
              <th>
                <AvatarFigure src={owner?.profile_url} alt={owner?.name} />
              </th>
              <td>
                <h5 className="ne__brick__title">
                  {ownerId ? (
                    <Link to={`/app/patients/${ownerId}/activity`}>
                      <strong>{owner?.name}</strong>
                    </Link>
                  ) : (
                    <strong>{owner?.name || item.key}</strong>
                  )}{" "}
                  {isAppointment(item) ? (
                    <span>{appointmentVerb(item)}</span>
                  ) : (
                    <span>{item.key}</span>
                  )}
                </h5>
                {isNewPatient ? (
                  <p className="ne__badge ne__badge--text ne__badge--secondary">
                    New Patient
                  </p>
                ) : null}
              </td>
            </tr>
          </thead>
          <tbody>
            {owner?.bio?.date_of_birth ? (
              <tr>
                <th>Birthday</th>
                <td>{formatMediumDate(owner.bio.date_of_birth)}</td>
              </tr>
            ) : null}
            {owner?.bio?.gender ? (
              <tr>
                <th>Gender</th>
                <td>{owner.bio.gender}</td>
              </tr>
            ) : null}
            {owner?.email ? (
              <tr>
                <th>Email</th>
                <td>
                  <div className="ne__brick__email">{owner.email}</div>
                </td>
              </tr>
            ) : null}
            {owner?.bio?.phone_number || owner?.phone ? (
              <tr>
                <th>Phone</th>
                <td>
                  {formatPhone(owner?.bio?.phone_number || owner?.phone)}
                </td>
              </tr>
            ) : null}
            <tr className="ne__divider">
              <th />
              <td />
            </tr>
            {item.parameters?.data ? (
              <tr>
                <th>Starts</th>
                <td>
                  <span>
                    {formatApptDate(String(item.parameters.data))}{" "}
                    {(() => {
                      const p = formatTimeParts(String(item.parameters.data));
                      return p.time ? (
                        <>
                          <span className="ne__time">{p.time}</span>
                          <span className="ne__period"> {p.period}</span>
                        </>
                      ) : null;
                    })()}
                  </span>
                </td>
              </tr>
            ) : null}
            <tr className="ne__divider">
              <th />
              <td />
            </tr>
            {item.parameters?.provider_name ? (
              <tr>
                <th>Provider</th>
                <td>{item.parameters.provider_name}</td>
              </tr>
            ) : null}
            {owner?.bio?.insurance ? (
              <tr>
                <th>Insurance</th>
                <td>{owner.bio.insurance}</td>
              </tr>
            ) : null}
            {locationName ? (
              <tr>
                <th>Location</th>
                <td>{locationName}</td>
              </tr>
            ) : null}
            {typeof item.trackable?.note === "string" && item.trackable.note ? (
              <tr>
                <th>Office Notes</th>
                <td>{item.trackable.note}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
        {isAppointment(item) &&
        (item.activity_type === "AppointmentCreate" ||
          item.activity_type === "AppointmentResched" ||
          item.key?.toLowerCase().includes("request") ||
          item.key?.toLowerCase().includes("resched")) ? (
          <div className="ne__brick__buttons">
            <a className="ne__link ne__link--primary" href="#cancel">
              Cancel
            </a>
            <a className="ne__link ne__link--primary" href="#details">
              Details
            </a>
            <button className="ne__btn ne__btn--primary ne__btn--md" type="button">
              Confirm
            </button>
          </div>
        ) : null}
      </div>
      </div>
    </li>
  );
}
